import {
  Box,
  Button,
  Icon,
  Modal,
  ModalCloseButton,
  ModalContent,
  Theme,
  Typography,
  styled,
} from "@outschool/backpack";
import { TeacherVideoModal_TeacherFragment } from "@outschool/gql-frontend-generated";
import { faArrowLeft, faArrowRight, faUser } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { leaderPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import { gql } from "@outschool/ui-apollo";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import SimpleVideo from "./SimpleVideo";

interface TeacherVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacher: TeacherVideoModal_TeacherFragment;
  MessageTeacherButton?: React.JSX.Element;
  onNext?: () => void;
  onPrevious?: () => void;
}

const StyledModalCloseButton = styled(ModalCloseButton)({
  position: "absolute",
  top: 0,
  right: 0,
  fontSize: "1.6rem",
});

export const TeacherVideoModal = ({
  isOpen,
  onClose,
  teacher,
  MessageTeacherButton,
  onNext,
  onPrevious,
}: TeacherVideoModalProps) => {
  const Link = useLinkComponent();
  const { t } = useTranslation("ui-components-website\\src\\TeacherVideoModal");
  const trackEvent = useTrackEvent();

  if (!isOpen || !teacher.video) {
    return null;
  }

  if (isOpen && teacher.video) {
    trackEvent(`class_details_class_video_view`);
  }
  const { name, video } = teacher;
  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalContent
        sx={(theme: Theme) => ({
          position: "relative",
          padding: "1em",
          paddingTop: "2em",
          [theme.breakpoints.up("md")]: {
            padding: "2em",
          },
        })}
      >
        <StyledModalCloseButton onClick={onClose} aria-label={t`Close`} />
        <Box
          flex
          sx={(theme: Theme) => ({
            flexDirection: "column",
            rowGap: "1em",
            justifyContent: "space-evenly",
            [theme.breakpoints.up("md")]: {
              rowGap: "1.5em",
            },
          })}
        >
          {name ? (
            <Typography variant="h3">
              {t("About {{name}}", { name })}
            </Typography>
          ) : null}
          <SimpleVideo
            title={t("About {{name}}", { name })}
            src={video.file.url}
            poster={video.file.thumbUrl ?? undefined}
            autoPlay
          />
          <Box
            flex
            sx={{ justifyContent: "space-between", gap: 8, "> *": { flex: 1 } }}
          >
            {onPrevious && (
              <Button
                startIcon={<Icon icon={faArrowLeft} />}
                onClick={onPrevious}
                sx={{
                  flex: "0 1",
                  paddingX: "12px",
                  "&>span": { marginRight: 0 },
                }}
              />
            )}
            <Button
              component={Link}
              size="small"
              startIcon={<Icon icon={faUser} />}
              target="_blank"
              trackingName="teacher_video_modal_profile_link"
              to={leaderPath(teacher.uid, teacher.leader_link)}
              sx={onPrevious && onNext ? { flexGrow: "0" } : {}}
            >
              {t`Profile`}
            </Button>
            {MessageTeacherButton}
            {onNext && (
              <Button
                startIcon={<Icon icon={faArrowRight} />}
                onClick={onNext}
                sx={{
                  flex: "0 1",
                  paddingX: "12px",
                  "&>span": { marginRight: 0 },
                }}
              />
            )}
          </Box>
        </Box>
      </ModalContent>
    </Modal>
  );
};

TeacherVideoModal.fragments = {
  teacher: gql`
    fragment TeacherVideoModal_teacher on Leader {
      uid
      name
      photo
      leader_link
      video {
        file {
          url
          thumbUrl
        }
      }
    }
  `,
};
